import { useEffect, useState } from "react";
import { useDeepCompareEffect, useMemoizedFn } from "ahooks";
import { commonFilterOptions, CommonOptionsForm, DemandDetailsResDto, outsourceCommonFilterOptions } from "#services/api";
import { $sType } from "#utils";
import { noop } from "@bedrock/components/lib/utils";


const filterOptionsApiMap = {
  inner: commonFilterOptions,
  outer: outsourceCommonFilterOptions
}

const cacheData = {}

export function cacheFilterOptions(params: CommonOptionsForm, cache = true) {
  if (JSON.stringify(params) in cacheData && cache) {
    return Promise.resolve(cacheData[JSON.stringify(params)])
  }
  return filterOptionsApiMap[$sType](params).then(res => {
    if (res.code === 1) {
      cacheData[JSON.stringify(params)] = res.data
      return res.data
    }
  })
}

type Props = {
  keys: string[] // 需要获取的options的key
  demandDetail?: DemandDetailsResDto, // 需求单详情
  cache?: boolean, // 是否缓存
  params?: CommonOptionsForm, // 可以传递的额外参数
  manual?: boolean // 是否手动触发
  onSuccess?: ({
    optionsMap,
    optionsListMap,
  }: {
    optionsMap: OptionsMap,
    optionsListMap: OptionsListMap,
  }) => void // 成功回调
}

type OptionsMap = Record<string, {
  key: string,
  name: string,
  options: any[]
}>

type OptionsListMap = Record<string, any[]>

export const useDemandOptions = ({
  keys, // 需要获取的options的key
  demandDetail, // 需求单详情
  params, // 可以传递的额外参数
  cache = true,
  manual = false, // 手动
  onSuccess = noop, // 成功回调
}: Props) => {
  const [optionsMap, setOptionsMap] = useState<OptionsMap>({})
  const [optionsListMap, setOptionsListMap] = useState<OptionsListMap>({})
  const [triggerKey, setTrigger] = useState(0)


  const syncOptionsMap = useMemoizedFn((data: OptionsMap, success: boolean) => {
    const listMap = Object.keys(data).reduce((acc, cur) => {
      acc[cur] = data[cur].options
      return acc
    }, {})
    setOptionsMap(data)    
    setOptionsListMap(listMap)
    if (success) {
      onSuccess({
        optionsMap: data,
        optionsListMap: listMap
      })
    }
  })

  useDeepCompareEffect(() => {
    if (manual && !triggerKey) return
    const sendParams: CommonOptionsForm = {
      keys,
    }

    if (demandDetail?.group_type?.toUpperCase?.() === 'CD_TRUMPET_GROUP') {
      // 小喇叭
      sendParams.group_type = 'CD_TRUMPET_GROUP';
      demandDetail?.state && (sendParams.trumpet_state = demandDetail.state.toUpperCase());
      demandDetail?.id && (sendParams.trumpet_demand_id = demandDetail.id);
    } else {
      // 常規需求单
      sendParams.group_type = demandDetail?.group_type?.toUpperCase?.() || "CHANNEL_GROUP";
      demandDetail?.state && (sendParams.demand_state = demandDetail.state.toUpperCase());
      demandDetail?.id && (sendParams.demand_id = demandDetail.id);
    }
    Object.assign(sendParams, params)
    console.log('sendParams', sendParams);
    
    cacheFilterOptions(sendParams, cache).then(data => {
      const dataMap = data?.reduce((acc, cur) => {
        acc[cur.key] = cur
        return acc
      }, {})
      syncOptionsMap(dataMap, true)
    })
  }, [
    keys,
    demandDetail,
    cache,
    params,
    manual,
    triggerKey
  ])

  useDeepCompareEffect(() => {
    // 兜底optionsMap
    const getKeys = params?.keys || keys || []
    if (Object.keys(optionsMap).length === 0 && getKeys.length > 0) {
      const dataMap = getKeys.reduce((acc, cur) => {
        acc[cur] = {
          key: cur,
          name: '加载中',
          options: []
        }
        return acc
      }, {})
      syncOptionsMap(dataMap, false)
    }
  }, [optionsMap, keys, params?.keys])

  const getOptionsMap = useMemoizedFn(() => {
    setTrigger(triggerKey + 1)
  })

  return { optionsListMap, optionsMap, getOptionsMap, onSuccess }
}

